import { useState, useEffect, useMemo } from 'react'
import { Copy, Check, Loader2, Trash2, X } from '@/ui/icons'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Button,
  Label,
  Card,
  TogglePill,
  Sheet,
  SheetContent,
  SheetBody
} from '@/ui/components'
import { useShareLinks } from '@/modules/trips/hooks/useShareLinks'
import { useSubscription } from '@/modules/subscriptions/hooks/useSubscription'
import { AnnouncementsService } from '@/modules/announcements/services/announcementsService'
import { ShareLinkResponse, ShareLinkPermissions, permissionPresets as shareLinkPresets } from '@/shared/api/shareLinks'
import { TripWithDetailsResponse } from '@/shared/api'
import { TripService } from '@/modules/trips/services/tripService'
import { useAuth } from '@/modules/auth'
import { SharePreview } from './SharePreview'
import { cn } from '@/shared/utils'

interface ShareModalProps {
  isOpen: boolean
  onClose: () => void
  tripId: string
}

type PermissionPreset = 'fullAccess' | 'basicInfo' | 'minimal' | 'custom'

function normalizeSharePermissions(
  perms: Partial<ShareLinkPermissions> & { viewNotes?: boolean } | undefined,
  coordinatesLocked = false
): ShareLinkPermissions {
  const legacy = perms as { viewNotes?: boolean } | undefined
  return {
    viewItinerary: perms?.viewItinerary ?? false,
    viewCampsiteCoordinates: coordinatesLocked
      ? true
      : (perms?.viewCampsiteCoordinates ?? false),
    viewCrew: perms?.viewCrew ?? false,
    viewAnnouncements: perms?.viewAnnouncements ?? legacy?.viewNotes ?? false,
    viewRadioChannel: perms?.viewRadioChannel ?? false,
    viewMeetupLocation: perms?.viewMeetupLocation ?? false
  }
}

function detectPresetFromPermissions(perms: ShareLinkPermissions): PermissionPreset {
  if (JSON.stringify(perms) === JSON.stringify(shareLinkPresets.fullAccess)) return 'fullAccess'
  if (JSON.stringify(perms) === JSON.stringify(shareLinkPresets.basicInfo)) return 'basicInfo'
  if (JSON.stringify(perms) === JSON.stringify(shareLinkPresets.minimal)) return 'minimal'
  return 'custom'
}

const PRESET_LABELS: Record<Exclude<PermissionPreset, 'custom'>, string> = {
  fullAccess: 'Full Access',
  basicInfo: 'Basic Info',
  minimal: 'Minimal'
}

function PermissionPresets({
  permissions,
  preset,
  onApplyPreset,
  onPresetChange,
  onPermissionsChange,
  coordinatesLocked = false
}: {
  permissions: ShareLinkPermissions
  preset: PermissionPreset
  onApplyPreset: (preset: PermissionPreset) => void
  onPresetChange: (preset: PermissionPreset) => void
  onPermissionsChange: (perms: ShareLinkPermissions) => void
  coordinatesLocked?: boolean
}) {
  const [showAdvanced, setShowAdvanced] = useState(false)

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-sm font-medium mb-2 block">Permission level</Label>
        <div className="grid grid-cols-3 gap-2">
          <button
            type="button"
            onClick={() => onApplyPreset('fullAccess')}
            className={cn(
              'flex flex-col gap-0.5 p-3 rounded-lg border-2 transition-all text-left',
              preset === 'fullAccess'
                ? 'border-adventure-700 bg-adventure-50'
                : 'border-earth-200 hover:border-earth-300'
            )}
          >
            <span className="font-medium text-sm">Full Access</span>
            <span className="text-xs text-muted-foreground">All details including exact locations</span>
          </button>

          <button
            type="button"
            onClick={() => onApplyPreset('basicInfo')}
            className={cn(
              'flex flex-col gap-0.5 p-3 rounded-lg border-2 transition-all text-left',
              preset === 'basicInfo'
                ? 'border-adventure-700 bg-adventure-50'
                : 'border-earth-200 hover:border-earth-300'
            )}
          >
            <span className="font-medium text-sm">Basic Info</span>
            <span className="text-xs text-muted-foreground">General area and dates, coordinates hidden</span>
          </button>

          <button
            type="button"
            onClick={() => onApplyPreset('minimal')}
            className={cn(
              'flex flex-col gap-0.5 p-3 rounded-lg border-2 transition-all text-left',
              preset === 'minimal'
                ? 'border-adventure-700 bg-adventure-50'
                : 'border-earth-200 hover:border-earth-300'
            )}
          >
            <span className="font-medium text-sm">Minimal</span>
            <span className="text-xs text-muted-foreground">Trip name and dates only</span>
          </button>
        </div>
      </div>

      <button
        type="button"
        onClick={() => setShowAdvanced(v => !v)}
        className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline transition-colors"
      >
        {showAdvanced ? 'Hide advanced options' : 'Advanced: customize permissions'}
      </button>

      {showAdvanced && (
        <div className="space-y-0.5">
          {([
            {
              key: 'viewCampsiteCoordinates' as const,
              label: 'Show exact locations',
              desc: coordinatesLocked
                ? 'Free plan always shares exact locations — upgrade to Pro to hide them'
                : 'GPS coordinates and detailed campsite information',
              locked: coordinatesLocked
            },
            {
              key: 'viewItinerary' as const,
              label: 'Show full itinerary',
              desc: 'Day-by-day schedule and activities',
              locked: false
            },
            {
              key: 'viewCrew' as const,
              label: 'Show participants',
              desc: "Who's going and vehicle information",
              locked: false
            },
            {
              key: 'viewAnnouncements' as const,
              label: 'Show announcements',
              desc: 'Trip announcements and bulletin board posts',
              locked: false
            },
            {
              key: 'viewRadioChannel' as const,
              label: 'Show radio channel',
              desc: 'Radio channel, frequency, and type information',
              locked: false
            },
            {
              key: 'viewMeetupLocation' as const,
              label: 'Show meetup location',
              desc: 'Meetup point from itinerary',
              locked: false
            }
          ]).map(({ key, label, desc, locked }) => (
            <div
              key={key}
              className="flex items-center justify-between rounded-md px-2 py-1.5 hover:bg-earth-100"
            >
              <div className="flex-1 min-w-0 mr-3">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-medium">{label}</span>
                  {locked && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-earth-100 text-earth-700">
                      Pro
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{desc}</p>
              </div>
              <TogglePill
                checked={locked ? true : permissions[key]}
                disabled={locked}
                onCheckedChange={(checked) => {
                  if (locked) return
                  onPermissionsChange({ ...permissions, [key]: checked })
                  onPresetChange('custom')
                }}
                aria-label={`Toggle ${label.toLowerCase()}`}
                className="flex-shrink-0"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const MOBILE_BREAKPOINT = 767

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`)
    const update = () => setIsMobile(mql.matches)
    update()
    mql.addEventListener('change', update)
    return () => mql.removeEventListener('change', update)
  }, [])
  return isMobile
}

export function ShareModal({ isOpen, onClose, tripId }: ShareModalProps) {
  const isMobile = useIsMobile()
  const { shareLinks, createShareLink, revokeShareLink, updateShareLink, isLoading } = useShareLinks(tripId)
  const { getAccessToken } = useAuth()
  const { isFree } = useSubscription()
  const coordinatesLocked = isFree

  const [tripData, setTripData] = useState<TripWithDetailsResponse | null>(null)
  const [isLoadingTrip, setIsLoadingTrip] = useState(false)

  const [showCreateForm, setShowCreateForm] = useState(false)
  const [showCreatePreview, setShowCreatePreview] = useState(false)
  const [sharePermissions, setSharePermissions] = useState<ShareLinkPermissions>(shareLinkPresets.basicInfo)
  const [sharePreset, setSharePreset] = useState<PermissionPreset>('basicInfo')
  const [allowJoin, setAllowJoin] = useState(false)

  const tripService = useMemo(() => new TripService(getAccessToken), [getAccessToken])
  const announcementsService = useMemo(() => new AnnouncementsService(getAccessToken), [getAccessToken])

  useEffect(() => {
    if (isOpen && tripId) {
      loadTripData()
    }
  }, [isOpen, tripId])

  const loadTripData = async () => {
    if (!tripId) return
    setIsLoadingTrip(true)
    try {
      const [trip, announcements] = await Promise.all([
        tripService.getTrip(tripId),
        announcementsService.getTripAnnouncements(tripId).catch(() => [])
      ])
      setTripData({ ...trip, announcements })
    } catch {
      setTripData(null)
    } finally {
      setIsLoadingTrip(false)
    }
  }

  const handleApplyCreatePreset = (preset: PermissionPreset) => {
    if (preset === 'custom') return
    const base = shareLinkPresets[preset as Exclude<PermissionPreset, 'custom'>]
    const withCoords = coordinatesLocked ? { ...base, viewCampsiteCoordinates: true } : base
    setSharePermissions(withCoords)
    setSharePreset(preset)
  }

  const handleCreateLink = async () => {
    const result = await createShareLink({
      permissions: coordinatesLocked
        ? { ...sharePermissions, viewCampsiteCoordinates: true }
        : sharePermissions,
      allow_join: allowJoin
    })

    if (result) {
      setShowCreateForm(false)
      setShowCreatePreview(false)
      setSharePermissions(shareLinkPresets.basicInfo)
      setSharePreset('basicInfo')
      setAllowJoin(false)
    }
  }

  const [copiedLink, setCopiedLink] = useState(false)

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    setCopiedLink(true)
    setTimeout(() => setCopiedLink(false), 2000)
  }

  const shareLinksContent = (
    <div className="space-y-4 mt-4">
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-medium">Share links</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Recipients can view and optionally join as a guest or member.
            </p>
          </div>
          {!showCreateForm && (
            <Button
              onClick={() => setShowCreateForm(true)}
              variant="outline"
              size="sm"
              disabled={isLoading}
              className="flex-shrink-0 min-h-[44px]"
            >
              Create link
            </Button>
          )}
        </div>

        {/* Existing links */}
        {shareLinks.length > 0 && (
          <div className="space-y-2">
            {shareLinks.map((link) => (
              <ShareLinkCard
                key={link.id}
                link={link}
                tripData={tripData}
                isLoadingTrip={isLoadingTrip}
                coordinatesLocked={coordinatesLocked}
                onRevoke={async () => { await revokeShareLink(link.id) }}
                onCopy={() => copyToClipboard(`${window.location.origin}/share/${link.token}`)}
                onUpdateAllowJoin={async (aj) => { await updateShareLink(link.id, { allow_join: aj }) }}
                onUpdateShareLink={async (data) => { await updateShareLink(link.id, data) }}
              />
            ))}
          </div>
        )}

        {/* Create form */}
        {showCreateForm && (
          <Card className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-sm">New share link</h4>
              <Button
                onClick={() => {
                  setShowCreateForm(false)
                  setShowCreatePreview(false)
                  setSharePermissions(shareLinkPresets.basicInfo)
                  setSharePreset('basicInfo')
                  setAllowJoin(false)
                }}
                variant="ghost"
                size="sm"
                className="min-h-[44px]"
              >
                Cancel
              </Button>
            </div>

            <PermissionPresets
              permissions={sharePermissions}
              preset={sharePreset}
              onApplyPreset={handleApplyCreatePreset}
              onPresetChange={setSharePreset}
              onPermissionsChange={setSharePermissions}
              coordinatesLocked={coordinatesLocked}
            />

            {/* Allow join toggle */}
            <div className="flex items-center justify-between rounded-md px-2 py-1.5 hover:bg-earth-100">
              <div className="flex-1 min-w-0 mr-3">
                <span className="text-sm font-medium">Allow viewers to join</span>
                <p className="text-xs text-muted-foreground">
                  Recipients can join as a guest or sign in as a member
                </p>
              </div>
              <TogglePill
                checked={allowJoin}
                onCheckedChange={setAllowJoin}
                aria-label="Allow viewers to join this trip"
                className="flex-shrink-0"
              />
            </div>

            {/* Expiration */}
            <p className="text-xs text-muted-foreground">
              {tripData?.end_date
                ? `Expires when trip ends (${new Date(tripData.end_date).toLocaleDateString()})`
                : "Trip has no end date — link won't expire"}
            </p>

            {/* Preview toggle */}
            <button
              type="button"
              onClick={() => setShowCreatePreview(v => !v)}
              className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline transition-colors"
            >
              {showCreatePreview ? 'Hide preview' : 'Preview what recipients will see'}
            </button>

            {showCreatePreview && (
              <SharePreview
                trip={tripData}
                permissions={sharePermissions}
                isLoading={isLoadingTrip}
              />
            )}

            <Button
              onClick={handleCreateLink}
              disabled={isLoading}
              className="w-full min-h-[44px]"
            >
              Create link
            </Button>
          </Card>
        )}

        {/* Empty state */}
        {shareLinks.length === 0 && !showCreateForm && (
          <div className="border border-dashed border-earth-200 rounded-lg p-8 text-center">
            <p className="text-sm font-medium text-foreground mb-1">No share links yet</p>
            <p className="text-xs text-muted-foreground mb-4">
              Create a link to give read-only access to your trip
            </p>
            <Button
              onClick={() => setShowCreateForm(true)}
              variant="outline"
              size="sm"
              disabled={isLoading}
              className="min-h-[44px]"
            >
              Create link
            </Button>
          </div>
        )}
      </div>
    </div>
  )

  if (isMobile) {
    return (
      <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <SheetContent side="bottom" className="p-0 gap-0 flex flex-col max-h-[90dvh]">
          <div className="flex-shrink-0 flex justify-center pt-2 pb-1" aria-hidden>
            <div className="w-10 h-1 rounded-full bg-earth-300" />
          </div>
          <div className="flex-shrink-0 flex items-center justify-between gap-2 px-4 pt-2 pb-2 border-b border-earth-200">
            <h2 className="text-lg font-semibold text-foreground">Share trip</h2>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="min-h-[44px] min-w-[44px]"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          <SheetBody className="flex-1 overflow-y-auto overscroll-contain px-4 pb-4 pt-2">
            {shareLinksContent}
          </SheetBody>
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-earth-50 border-earth-700 text-foreground">
        <DialogHeader>
          <DialogTitle className="text-foreground">Share trip</DialogTitle>
        </DialogHeader>
        {shareLinksContent}
      </DialogContent>
    </Dialog>
  )
}

function ShareLinkCard({
  link,
  tripData,
  isLoadingTrip,
  onRevoke,
  onCopy,
  onUpdateAllowJoin,
  onUpdateShareLink,
  coordinatesLocked = false
}: {
  link: ShareLinkResponse
  tripData: TripWithDetailsResponse | null
  isLoadingTrip: boolean
  onRevoke: () => Promise<void>
  onCopy: () => void
  onUpdateAllowJoin?: (allowJoin: boolean) => Promise<void>
  onUpdateShareLink: (data: { permissions: ShareLinkPermissions; allow_join: boolean }) => Promise<void>
  coordinatesLocked?: boolean
}) {
  const [showConfirm, setShowConfirm] = useState(false)
  const [isRevoking, setIsRevoking] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [copied, setCopied] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [showEditPreview, setShowEditPreview] = useState(false)
  const [editPermissions, setEditPermissions] = useState<ShareLinkPermissions>(shareLinkPresets.basicInfo)
  const [editPreset, setEditPreset] = useState<PermissionPreset>('basicInfo')
  const [editAllowJoin, setEditAllowJoin] = useState(false)
  const [isSavingEdit, setIsSavingEdit] = useState(false)

  const beginEdit = () => {
    const normalized = normalizeSharePermissions(link.permissions, coordinatesLocked)
    setEditPermissions(normalized)
    setEditPreset(detectPresetFromPermissions(normalized))
    setEditAllowJoin(link.allow_join === true)
    setIsEditing(true)
  }

  const handleApplyEditPreset = (preset: PermissionPreset) => {
    if (preset === 'custom') return
    const base = shareLinkPresets[preset as Exclude<PermissionPreset, 'custom'>]
    const withCoords = coordinatesLocked ? { ...base, viewCampsiteCoordinates: true } : base
    setEditPermissions(withCoords)
    setEditPreset(preset)
  }

  const handleSaveEdit = async () => {
    setIsSavingEdit(true)
    try {
      await onUpdateShareLink({
        permissions: coordinatesLocked
          ? { ...editPermissions, viewCampsiteCoordinates: true }
          : editPermissions,
        allow_join: editAllowJoin
      })
      setIsEditing(false)
      setShowEditPreview(false)
    } finally {
      setIsSavingEdit(false)
    }
  }

  const handleCopy = () => {
    onCopy()
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const normalizedPerms = normalizeSharePermissions(link.permissions, coordinatesLocked)
  const detectedPreset = detectPresetFromPermissions(normalizedPerms)

  const permissionLabel = detectedPreset !== 'custom'
    ? PRESET_LABELS[detectedPreset]
    : (() => {
        const active = Object.entries(link.permissions)
          .filter(([_, v]) => v)
          .map(([key]) => ({
            viewItinerary: 'Itinerary',
            viewCampsiteCoordinates: 'Coordinates',
            viewCrew: 'Crew',
            viewAnnouncements: 'Announcements',
            viewRadioChannel: 'Radio',
            viewMeetupLocation: 'Meetup'
          }[key] || key))
        return active.length > 0 ? `Custom — ${active.join(', ')}` : 'Custom — no permissions'
      })()

  const hasAllowJoin = link.allow_join === true
  const isExpired = !!(link.expires_at && new Date(link.expires_at) < new Date())
  const expiresAtDate = link.expires_at ? new Date(link.expires_at) : null
  const now = new Date()

  const getExpirationStatus = () => {
    if (!expiresAtDate) return { text: 'Never expires', color: 'text-muted-foreground' }
    if (isExpired) return { text: 'Expired', color: 'text-destructive' }
    const diffDays = Math.ceil((expiresAtDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
    if (diffDays <= 1) return { text: 'Expires soon', color: 'text-warning' }
    if (diffDays <= 7) return { text: `Expires in ${diffDays} days`, color: 'text-earth-700' }
    return { text: `Expires ${expiresAtDate.toLocaleDateString()}`, color: 'text-muted-foreground' }
  }

  const expirationStatus = getExpirationStatus()

  if (isEditing) {
    return (
      <Card className="p-4 border-adventure-200 space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h4 className="font-semibold text-sm text-foreground">Edit share link</h4>
            <p className="text-xs text-muted-foreground mt-0.5">
              Changes apply to everyone who uses this link — the URL stays the same.
            </p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="min-h-[44px]"
              onClick={() => { setIsEditing(false); setShowEditPreview(false) }}
              disabled={isSavingEdit}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              className="min-h-[44px]"
              onClick={() => void handleSaveEdit()}
              disabled={isSavingEdit}
            >
              {isSavingEdit ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  Saving
                </>
              ) : (
                'Save changes'
              )}
            </Button>
          </div>
        </div>

        <PermissionPresets
          permissions={editPermissions}
          preset={editPreset}
          onApplyPreset={handleApplyEditPreset}
          onPresetChange={setEditPreset}
          onPermissionsChange={setEditPermissions}
          coordinatesLocked={coordinatesLocked}
        />

        <div className="flex items-center justify-between rounded-md px-2 py-1.5 hover:bg-earth-100">
          <div className="flex-1 min-w-0 mr-3">
            <span className="text-sm font-medium">Allow viewers to join</span>
            <p className="text-xs text-muted-foreground">
              Recipients can join as a guest or sign in as a member
            </p>
          </div>
          <TogglePill
            checked={editAllowJoin}
            onCheckedChange={setEditAllowJoin}
            aria-label="Allow viewers to join this trip"
            className="flex-shrink-0"
          />
        </div>

        <p className="text-xs text-muted-foreground">
          {tripData?.end_date
            ? `Expires when trip ends (${new Date(tripData.end_date).toLocaleDateString()})`
            : "Trip has no end date — link won't expire"}
        </p>

        <button
          type="button"
          onClick={() => setShowEditPreview(v => !v)}
          className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline transition-colors"
        >
          {showEditPreview ? 'Hide preview' : 'Preview what recipients will see'}
        </button>

        {showEditPreview && (
          <SharePreview
            trip={tripData}
            permissions={editPermissions}
            isLoading={isLoadingTrip}
          />
        )}
      </Card>
    )
  }

  return (
    <Card className={cn('p-4', isExpired && 'opacity-60 border-destructive/30')}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0 space-y-1.5">
          <p className="text-sm font-medium">{permissionLabel}</p>

          <div className="flex flex-wrap items-center gap-2">
            {hasAllowJoin && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-adventure-100 text-adventure-800">
                Can join
              </span>
            )}
            <span className={cn('text-xs', expirationStatus.color)}>
              {expirationStatus.text}
            </span>
            <span className="text-xs text-muted-foreground">
              {link.view_count} {link.view_count === 1 ? 'view' : 'views'}
            </span>
          </div>

          {/* Allow join quick-toggle */}
          {onUpdateAllowJoin && !isExpired && (
            <div className="flex items-center justify-between gap-2 pt-2 border-t border-earth-200">
              <span className="text-xs font-medium text-foreground">Allow viewers to join</span>
              <TogglePill
                checked={hasAllowJoin}
                onCheckedChange={async (checked) => {
                  setIsUpdating(true)
                  try {
                    await onUpdateAllowJoin(checked)
                  } finally {
                    setIsUpdating(false)
                  }
                }}
                disabled={isUpdating}
                aria-label="Allow viewers to join this trip"
                className="flex-shrink-0"
              />
            </div>
          )}
        </div>

        <div className="flex items-center gap-0.5 flex-shrink-0">
          {showConfirm ? (
            <div className="flex items-center gap-2">
              <span className="text-destructive text-xs font-medium whitespace-nowrap">Delete this link?</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowConfirm(false)}
                disabled={isRevoking}
                className="min-h-[44px]"
              >
                Cancel
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => {
                  setIsRevoking(true)
                  try {
                    await onRevoke()
                    setShowConfirm(false)
                  } finally {
                    setIsRevoking(false)
                  }
                }}
                disabled={isRevoking}
                className="min-h-[44px] text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                {isRevoking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Delete'}
              </Button>
            </div>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopy}
                disabled={isExpired}
                className="min-h-[44px]"
              >
                {copied ? (
                  <><Check className="w-3.5 h-3.5 mr-1.5 text-success" />Copied</>
                ) : (
                  'Copy link'
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={beginEdit}
                disabled={isExpired}
                className="min-h-[44px]"
              >
                Edit
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowConfirm(true)}
                className="min-h-[44px] text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </>
          )}
        </div>
      </div>
    </Card>
  )
}
